import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Clock,
  CheckCircle,
  XCircle,
  Loader2,
  ArrowRight,
  ArrowLeft,
  FileText,
  Image,
  Images,
  Film,
  Volume2,
  Music2,
  Download,
  Megaphone,
  CircleSlash,
  ChevronLeft,
  ChevronRight,
  Newspaper,
  BookOpen,
  Coins,
  RefreshCw,
  ScrollText,
  Mic,
  Users,
  Youtube,
  Instagram,
  MoreVertical,
  MessageSquare,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import api from '@/lib/api';
import { mediaUrlForPlayback } from '@/lib/mediaUrls';
import {
  canOpenJobInAgent,
  filterJobsForTaskScope,
  STORY_MASTER_JOB_TYPES,
  STORY_TASK_TYPE_FILTER_OPTIONS,
  TASK_STATUS_FILTERS,
  TASK_TYPE_FILTER_OPTIONS,
} from '@/lib/taskHistoryScope';
import { IMAGE_STORY_JOB_TYPE, imageStoryJobResumePath, imageStoryNarrationProjectId } from '@/lib/imageStoryGenerationJob';
import { MASCOT_ANIMATION_JOB_TYPE, mascotJobResumePath, mascotProjectIdFromJob } from '@/lib/mascotAnimation';
import { ScriptBasedPipelineDialog } from '@/components/ScriptBasedPipelineDialog';
import { SongBasedPipelineDialog } from '@/components/SongBasedPipelineDialog';
import { YouTubePublishDialog } from '@/components/YouTubePublishDialog';
import { InstagramPublishDialog } from '@/components/InstagramPublishDialog';
import {
  jobSupportsSocialPublish,
  resolveExportVersionPublishUrl,
  resolveTaskJobPublishVideoUrl,
  taskJobPublishTitle,
} from '@/lib/taskHistorySocialPublish';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Filter, X } from 'lucide-react';
import { leftTopArrowGradient, rightBottomArrowGradient } from '@/assets/svgs/gradientIcons';
import { CreatePlanChatStudio } from '@/components/createPlan/CreatePlanChatStudio';
import { CREATE_FLOW_PARAM, exportPlatformLabel, planFromJob } from '@/lib/creationPlan';

import { TwinneeLogo } from '@/components/TwinneeLogo';
const PAGE_SIZE = 15;
const POLL_ACTIVE_MS = 8000;
const JOBS_REQUEST_TIMEOUT_MS = 60_000;
const TASKS_CACHE_KEY = 'twinnee_tasks_cache_v3';

const TASK_SORT_OPTIONS = [
  { id: 'date_desc', label: 'Newest first' },
  { id: 'date_asc', label: 'Oldest first' },
  { id: 'name_asc', label: 'A–Z' },
  { id: 'name_desc', label: 'Z–A' },
];

function normalizeTaskSort(value) {
  const id = String(value || '').trim();
  return TASK_SORT_OPTIONS.some((o) => o.id === id) ? id : 'date_desc';
}

function tasksCacheKey(scope, statusFilter, typeFilter, page, projectFilter, sort) {
  return `${scope}|${statusFilter || ''}|${typeFilter || ''}|${page}|${projectFilter || ''}|${normalizeTaskSort(sort)}`;
}

function readTasksCache(scope, statusFilter, typeFilter, page, projectFilter, sort) {
  try {
    const raw = sessionStorage.getItem(TASKS_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.key !== tasksCacheKey(scope, statusFilter, typeFilter, page, projectFilter, sort)) return null;
    if (!Array.isArray(parsed.items)) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeTasksCache(scope, statusFilter, typeFilter, page, projectFilter, sort, items, total, exportSummary) {
  try {
    sessionStorage.setItem(TASKS_CACHE_KEY, JSON.stringify({
      key: tasksCacheKey(scope, statusFilter, typeFilter, page, projectFilter, sort),
      items,
      total,
      exportSummary: exportSummary || null,
      savedAt: Date.now(),
    }));
  } catch {
    /* quota / private mode */
  }
}

const typeIcons = {
  story: TwinneeLogo,
  pdf: FileText,
  image_regen: Image,
  video: Film,
  audio: Volume2,
  music: Music2,
  narration_background_music: Music2,
  bg_music_segments: Music2,
  narration_bg_music_segments: Music2,
  export: Download,
  ad: Megaphone,
  narration_pipeline: Film,
  script_based_pipeline: ScrollText,
  song_based_pipeline: Music2,
  narration_full_automation: TwinneeLogo,
  news_video_pipeline: Newspaper,
  scene_video: Film,
  scene_lipsync_video: Film,
  audio_scene: Volume2,
  learning_tutorial: BookOpen,
  learning_tutorial_render: Film,
  pitch_recording_pipeline: Mic,
  reels_shorts_pipeline: Film,
  reels_shorts_render: Film,
  script_audio_align_pipeline: BookOpen,
  avatar_cast_pipeline: Users,
  [IMAGE_STORY_JOB_TYPE]: Images,
  [MASCOT_ANIMATION_JOB_TYPE]: Users,
};
const typeLabels = {
  story: 'Story Generation',
  pdf: 'PDF Export',
  image_regen: 'Image Regeneration',
  video: 'Video Generation',
  audio: 'Audio Narration',
  music: 'Background Music',
  narration_background_music: 'Background music',
  bg_music_segments: 'Scene background music',
  narration_bg_music_segments: 'Narration segment background music',
  export: 'Video Export',
  ad: 'Ad Generation',
  narration_pipeline: 'Narration Video',
  script_based_pipeline: 'Script-based',
  song_based_pipeline: 'Song-based',
  narration_full_automation: 'Narration (full automation)',
  news_video_pipeline: 'News Video',
  scene_video: 'Scene video',
  scene_lipsync_video: 'Lip-sync video',
  audio_scene: 'Scene audio',
  learning_tutorial: 'Learning tutorial',
  learning_tutorial_render: 'Learning tutorial export',
  pitch_recording_pipeline: 'Pitch recording',
  reels_shorts_pipeline: 'REEL&SHORTS',
  reels_shorts_render: 'REEL&SHORTS final video',
  publish_video_autofill: 'Publish autofill',
  script_audio_align_pipeline: 'Tutorial / presentation',
  avatar_cast_pipeline: 'AvatarCast',
  [IMAGE_STORY_JOB_TYPE]: 'Image storyboard',
  [MASCOT_ANIMATION_JOB_TYPE]: 'News mascot animation',
};

const LIFECYCLE_LABELS = {
  draft: 'Draft',
  unpublished: 'Unpublished',
  exported: 'Exported',
  published: 'Published',
};

const LEARNING_JOB_TYPES = new Set(['learning_tutorial', 'learning_tutorial_render']);

const statusConfig = {
  pending: { icon: Clock, color: '#94A3B8', bg: 'bg-slate-100', label: 'Pending' },
  running: { icon: Loader2, color: '#6366F1', bg: 'bg-[#EEF2FF]', label: 'Running', animate: true },
  completed: { icon: CheckCircle, color: '#10B981', bg: 'bg-[#ECFDF5]', label: 'Completed' },
  failed: { icon: XCircle, color: '#FF6B6B', bg: 'bg-red-50', label: 'Failed' },
  cancelled: { icon: XCircle, color: '#94A3B8', bg: 'bg-slate-100', label: 'Cancelled' },
};

const CANCELLABLE_JOB_TYPES = new Set([
  'export', 'pdf', 'narration_pipeline', 'narration_full_automation', 'news_video_pipeline',
  'script_based_pipeline',
  'song_based_pipeline',
  'music', 'narration_background_music', 'bg_music_segments', 'narration_bg_music_segments', 'story', 'video', 'audio', 'ad', 'scene_video',
  'learning_tutorial_render',
  'reels_shorts_pipeline',
  'reels_shorts_render',
  IMAGE_STORY_JOB_TYPE,
]);

const RESTARTABLE_JOB_TYPES = new Set([
  'story', 'video', 'audio', 'music', 'pdf', 'scene_video', 'export',
  'narration_pipeline', 'narration_full_automation', 'script_based_pipeline', 'song_based_pipeline',
  'reels_shorts_render',
]);

/** Completed jobs that produce a downloadable video MP4 (beyond full export). */
const VIDEO_OUTPUT_JOB_TYPES = new Set([
  'export',
  'story',
  'scene_video',
  'scene_lipsync_video',
  'extend_video_from_last_frame',
  'narration_segment_video',
  'narration_segment_multishot',
  'video',
  'narration_pipeline',
  'narration_full_automation',
  'news_video_pipeline',
  'learning_tutorial_render',
  'reels_shorts_render',
  'avatar_cast_pipeline',
  'script_audio_align_pipeline',
]);

const TASK_SCOPES = [
  { id: 'all', label: 'All tasks' },
  { id: 'stories', label: 'Stories' },
];

function TaskSocialPublishButtons({ videoUrl, title, jobId, onOpen, testIdPrefix, compact = false }) {
  if (!videoUrl) return null;
  const open = (platform) => onOpen(platform, { videoUrl, title, jobId });
  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => open('youtube')}
        data-testid={`${testIdPrefix}-youtube`}
        className={compact ? 'h-7 px-2 text-xs gap-1' : 'dashboard-btn h-8 px-2 text-xs gap-1'}
        style={{ borderColor: 'var(--glass-border)', color: 'hsl(var(--muted-foreground))' }}
        title="Upload to YouTube"
      >
        <Youtube className="w-3.5 h-3.5 shrink-0" />
        {!compact ? <span className="hidden sm:inline">YouTube</span> : null}
      </Button>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => open('instagram')}
        data-testid={`${testIdPrefix}-instagram`}
        className={compact ? 'h-7 px-2 text-xs gap-1' : 'dashboard-btn h-8 px-2 text-xs gap-1'}
        style={{ borderColor: 'var(--glass-border)', color: 'hsl(var(--muted-foreground))' }}
        title="Publish as Instagram Reel"
      >
        <Instagram className="w-3.5 h-3.5 shrink-0" style={{ color: '#E1306C' }} />
        {!compact ? <span className="hidden sm:inline">Instagram</span> : null}
      </Button>
    </>
  );
}

export const TaskHistoryPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const highlightJobId = searchParams.get('job');
  const projectFilter = searchParams.get('project')
    || searchParams.get('narration_project_id')
    || searchParams.get('narration_project')
    || '';
  const [scope, setScope] = useState(() => searchParams.get('scope') || 'all');
  const [statusFilter, setStatusFilter] = useState(() => searchParams.get('status') || '');
  const [typeFilter, setTypeFilter] = useState(() => searchParams.get('type') || '');
  const [sortBy, setSortBy] = useState(() => normalizeTaskSort(searchParams.get('sort')));
  const [searchQuery, setSearchQuery] = useState('');
  const [isFilterDropdownOpen, setIsFilterDropdownOpen] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [total, setTotal] = useState(0);
  const [exportVersionsSummary, setExportVersionsSummary] = useState([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [cancellingId, setCancellingId] = useState(null);
  const [restartingId, setRestartingId] = useState(null);
  const [scriptPipelineTarget, setScriptPipelineTarget] = useState(null);
  const [songPipelineTarget, setSongPipelineTarget] = useState(null);
  const [socialPublish, setSocialPublish] = useState(null);
  const [openingAgentJobId, setOpeningAgentJobId] = useState(null);
  const agentParam = searchParams.get('agent') || '';
  const flowParam = searchParams.get(CREATE_FLOW_PARAM) || '';
  const jobsFetchOkRef = useRef(false);
  const fetchGenRef = useRef(0);
  const pollTimerRef = useRef(null);

  const loadJobs = useCallback(async ({ silent = false } = {}) => {
    const gen = fetchGenRef.current + 1;
    fetchGenRef.current = gen;
    const cache = readTasksCache(scope, statusFilter, typeFilter, page, projectFilter, sortBy);
    if (!silent && cache) {
      setJobs(cache.items);
      setTotal(typeof cache.total === 'number' ? cache.total : cache.items.length);
      setExportVersionsSummary(Array.isArray(cache.exportSummary) ? cache.exportSummary : []);
      setLoading(false);
      setRefreshing(true);
      jobsFetchOkRef.current = true;
    } else if (!silent) {
      setLoading(true);
    }

    try {
      const params = { page, limit: PAGE_SIZE, scope, sort: sortBy };
      if (statusFilter) params.status = statusFilter;
      if (typeFilter) params.job_type = typeFilter;
      if (projectFilter) params.narration_project_id = projectFilter;
      const { data } = await api.get('/jobs', {
        params,
        timeout: JOBS_REQUEST_TIMEOUT_MS,
      });
      if (gen !== fetchGenRef.current) return;

      const raw = Array.isArray(data?.items) ? data.items : [];
      const items = filterJobsForTaskScope(raw, scope);
      const apiTotal = typeof data?.total === 'number' ? data.total : items.length;
      const summary = Array.isArray(data?.export_versions_summary) ? data.export_versions_summary : [];
      const totalPagesNow = Math.max(1, Math.ceil(apiTotal / PAGE_SIZE));

      if (page > totalPagesNow) {
        setTotal(apiTotal);
        setPage(totalPagesNow);
        return;
      }

      setJobs(items);
      setTotal(apiTotal);
      setExportVersionsSummary(summary);
      writeTasksCache(scope, statusFilter, typeFilter, page, projectFilter, sortBy, items, apiTotal, summary);
      jobsFetchOkRef.current = true;

      const hasActive = items.some(
        (j) => j?.status === 'pending' || j?.status === 'running',
      );
      if (pollTimerRef.current) {
        clearTimeout(pollTimerRef.current);
        pollTimerRef.current = null;
      }
      if (hasActive) {
        pollTimerRef.current = setTimeout(() => {
          loadJobs({ silent: true });
        }, POLL_ACTIVE_MS);
      }
    } catch (err) {
      if (gen !== fetchGenRef.current) return;
      const cancelled = err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError';
      const timedOut = err?.code === 'ECONNABORTED';
      if (!cancelled && !jobsFetchOkRef.current) {
        toast.error(timedOut ? 'Tasks took too long to load — try again' : 'Failed to load tasks');
      }
    } finally {
      if (gen === fetchGenRef.current) {
        if (!silent) setLoading(false);
        setRefreshing(false);
      }
    }
  }, [page, scope, statusFilter, typeFilter, projectFilter, sortBy]);

  useEffect(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (scope && scope !== 'all') next.set('scope', scope);
      else next.delete('scope');
      if (statusFilter) next.set('status', statusFilter);
      else next.delete('status');
      if (typeFilter) next.set('type', typeFilter);
      else next.delete('type');
      if (projectFilter) next.set('project', projectFilter);
      else next.delete('project');
      if (sortBy && sortBy !== 'date_desc') next.set('sort', sortBy);
      else next.delete('sort');
      return next;
    }, { replace: true });
  }, [scope, statusFilter, typeFilter, projectFilter, sortBy, setSearchParams]);

  useEffect(() => {
    loadJobs({ silent: false });
    return () => {
      fetchGenRef.current += 1;
      if (pollTimerRef.current) {
        clearTimeout(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };
  }, [loadJobs]);

  const openCreateAgent = (planId = 'new') => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('agent', planId);
      return next;
    }, { replace: true });
  };

  const openDashboardAgent = (planId, fallbackStoryId = '') => {
    if (!planId) return;
    const params = new URLSearchParams();
    params.set('agent', planId);
    if (fallbackStoryId) params.set('story', fallbackStoryId);
    navigate(`/dashboard?${params.toString()}`);
  };

  const handleOpenInAgent = async (job) => {
    if (!job?.id || openingAgentJobId) return;
    const storyId =
      job.narration_project_id
      || job.project_id
      || job.story_id
      || job.pitch_project_id
      || job.mascot_project_id
      || '';
    if (job.agent_plan_id) {
      openDashboardAgent(job.agent_plan_id, storyId);
      return;
    }
    setOpeningAgentJobId(job.id);
    try {
      const plan = await planFromJob(job.id);
      if (!plan?.id) throw new Error('Could not open agent');
      openDashboardAgent(plan.id, plan.execution?.project_id || storyId);
      setJobs((prev) =>
        prev.map((row) =>
          row.id === job.id
            ? { ...row, agent_plan_id: plan.id, agent_flow_id: plan.flow?.id || plan.execution?.flow_id || '' }
            : row,
        ),
      );
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || 'Could not open in agent');
    } finally {
      setOpeningAgentJobId(null);
    }
  };

  const closeCreateAgent = () => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('agent');
      next.delete('story');
      return next;
    }, { replace: true });
  };

  const socialOAuthReturnPath = useMemo(() => {
    const params = new URLSearchParams();
    if (projectFilter) params.set('project', projectFilter);
    if (scope && scope !== 'all') params.set('scope', scope);
    if (statusFilter) params.set('status', statusFilter);
    if (typeFilter) params.set('type', typeFilter);
    if (socialPublish?.jobId) params.set('job', socialPublish.jobId);
    const q = params.toString();
    return `/tasks${q ? `?${q}` : ''}`;
  }, [projectFilter, scope, statusFilter, typeFilter, socialPublish?.jobId]);

  const openSocialPublish = (platform, { videoUrl, title, jobId, scriptContext = '' }) => {
    setSocialPublish({
      platform,
      videoUrl: videoUrl || '',
      title: title || '',
      scriptContext: scriptContext || '',
      jobId: jobId || null,
    });
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const msg = params.get('message');
    const yt = params.get('youtube');
    const ig = params.get('instagram');
    if (!yt && !ig) return;
    if (yt === 'connected') {
      toast.success('YouTube connected.');
      setSocialPublish((prev) => ({ ...(prev || {}), platform: 'youtube' }));
    } else if (yt === 'error') {
      toast.error(msg ? decodeURIComponent(msg) : 'YouTube connection failed');
    }
    if (ig === 'connected') {
      toast.success('Instagram connected.');
      setSocialPublish((prev) => ({ ...(prev || {}), platform: 'instagram' }));
    } else if (ig === 'error') {
      toast.error(msg ? decodeURIComponent(msg) : 'Instagram connection failed');
    }
    const next = new URLSearchParams(searchParams);
    next.delete('youtube');
    next.delete('instagram');
    next.delete('message');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const switchScope = (nextScope) => {
    if (nextScope === scope) return;
    setScope(nextScope);
    setTypeFilter('');
    setJobs([]);
    setTotal(0);
    setPage(1);
    setLoading(true);
    jobsFetchOkRef.current = false;
  };

  const applyStatusFilter = (nextStatus) => {
    if (nextStatus === statusFilter) return;
    setStatusFilter(nextStatus);
    setJobs([]);
    setTotal(0);
    setPage(1);
    setLoading(true);
    jobsFetchOkRef.current = false;
  };

  const applySortBy = (nextSort) => {
    const normalized = normalizeTaskSort(nextSort);
    if (normalized === sortBy) return;
    setSortBy(normalized);
    setJobs([]);
    setTotal(0);
    setPage(1);
    setLoading(true);
    jobsFetchOkRef.current = false;
  };

  const applyTypeFilter = (nextType) => {
    if (nextType === typeFilter) return;
    setTypeFilter(nextType);
    setJobs([]);
    setTotal(0);
    setPage(1);
    setLoading(true);
    jobsFetchOkRef.current = false;
  };

  const clearFilters = () => {
    setStatusFilter('');
    setTypeFilter('');
    setSearchQuery('');
    setPage(1);
    setLoading(true);
    jobsFetchOkRef.current = false;
    if (projectFilter) {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete('project');
        next.delete('narration_project_id');
        next.delete('narration_project');
        return next;
      }, { replace: true });
    }
  };

  const clearProjectFilter = () => {
    setPage(1);
    setLoading(true);
    jobsFetchOkRef.current = false;
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('project');
      next.delete('narration_project_id');
      next.delete('narration_project');
      return next;
    }, { replace: true });
  };

  const hasActiveFilters = Boolean(statusFilter || typeFilter || projectFilter || searchQuery);
  const isStoriesScope = scope === 'stories';
  const typeFilterOptions = isStoriesScope ? STORY_TASK_TYPE_FILTER_OPTIONS : TASK_TYPE_FILTER_OPTIONS;

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const rangeStart = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(page * PAGE_SIZE, total);

  const filteredJobs = useMemo(() => {
    let list = jobs;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      list = jobs.filter((job) => {
        const title = (job.context_title || typeLabels[job.job_type] || job.job_type || '').toLowerCase();
        const msg = (job.message || '').toLowerCase();
        const id = (job.id || '').toLowerCase();
        return title.includes(q) || msg.includes(q) || id.includes(q);
      });
    }
    const titleOf = (job) => (
      job.context_title || typeLabels[job.job_type] || job.job_type || ''
    ).toLowerCase();
    const dateOf = (job) => {
      const t = Date.parse(job.updated_at || job.created_at || '');
      return Number.isFinite(t) ? t : 0;
    };
    const sorted = [...list];
    if (sortBy === 'name_asc') {
      sorted.sort((a, b) => titleOf(a).localeCompare(titleOf(b)));
    } else if (sortBy === 'name_desc') {
      sorted.sort((a, b) => titleOf(b).localeCompare(titleOf(a)));
    } else if (sortBy === 'date_asc') {
      sorted.sort((a, b) => dateOf(a) - dateOf(b));
    } else {
      sorted.sort((a, b) => dateOf(b) - dateOf(a));
    }
    return sorted;
  }, [jobs, searchQuery, sortBy]);

  const cancelJob = async (jobId) => {
    setCancellingId(jobId);
    try {
      await api.post(`/jobs/${jobId}/cancel`);
      toast.success('Task cancelled');
      await loadJobs({ silent: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not cancel task');
    } finally {
      setCancellingId(null);
    }
  };

  const restartJob = async (jobId) => {
    setRestartingId(jobId);
    try {
      await api.post(`/jobs/${jobId}/restart`);
      toast.success('Task restarted — it is now queued again');
      await loadJobs({ silent: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not restart task');
    } finally {
      setRestartingId(null);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="min-h-screen bg-transparent text-white py-6 sm:py-12 px-3 sm:px-6 lg:px-10 relative overflow-x-hidden" data-testid="task-history-page">
      {/* ── Ambient SVG glows (vibrant & glowing) ── */}
      <div
        className="fixed top-0 left-0 w-[700px] lg:w-[900px] pointer-events-none z-0 select-none"
        style={{ opacity: 0.60, transform: 'translate(-20%, -20%)' }}
        dangerouslySetInnerHTML={{ __html: leftTopArrowGradient }}
      />
      <div
        className="fixed bottom-0 right-0 w-[600px] lg:w-[800px] pointer-events-none z-0 select-none"
        style={{ opacity: 0.60, transform: 'translate(20%, 20%)' }}
        dangerouslySetInnerHTML={{ __html: rightBottomArrowGradient }}
      />

      <div className="max-w-5xl mx-auto flex flex-col gap-5 sm:gap-6 relative z-10 animate-in fade-in duration-300">

        {/* ── Back Navigation ── */}
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <button
            type="button"
            onClick={() => {
              if (window.history.length > 1) {
                navigate(-1);
              } else {
                navigate('/dashboard');
              }
            }}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl border border-white/10 bg-white/[0.04] hover:bg-white/[0.08] hover:border-white/20 text-gray-300 hover:text-white text-xs sm:text-sm font-medium transition-all cursor-pointer shadow-sm active:scale-95 group"
          >
            <ArrowLeft className="w-4 h-4 text-gray-400 group-hover:text-[#62FAE3] group-hover:-translate-x-0.5 transition-all" />
            <span>Back</span>
          </button>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => openCreateAgent('new')}
              className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-xl text-black text-xs sm:text-sm font-bold transition-all shadow-[0_0_15px_rgba(98,250,227,0.3)] cursor-pointer active:scale-95"
              style={{ background: 'linear-gradient(106.58deg, #D0BCFF 0%, #62FAE3 100%)' }}
              data-testid="tasks-open-create-agent"
            >
              <TwinneeLogo className="w-4 h-4" />
              <span>Create with agent</span>
            </button>
          </div>
        </div>

        {/* ── Figma Header ── */}
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black font-headline tracking-tight text-white flex items-center gap-2.5">
              <span>Task</span>
              <span
                className="bg-clip-text text-transparent"
                style={{
                  backgroundImage: 'linear-gradient(106.58deg, #D0BCFF 0%, #62FAE3 100%)',
                }}
              >
                History
              </span>
              <span
                className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] sm:text-xs font-mono font-semibold border border-[#62FAE3]/30 bg-[#62FAE3]/10 ml-1.5 self-end mb-1 sm:mb-1.5 shadow-[0_0_10px_rgba(98,250,227,0.12)] brand-gradient-text"
              >
                {total} Total
              </span>
            </h1>
            {refreshing && (
              <span className="inline-flex items-center gap-1.5 text-xs text-[#22D3EE] font-mono animate-pulse">
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                Updating...
              </span>
            )}
          </div>
          <p className="text-sm text-gray-400 font-body">
            Track and manage your generation jobs across the studio.
          </p>
        </div>

        {/* ── Main Controls Bar (Figma Exact) ── */}
        <div className="rounded-2xl border border-white/[0.08] bg-[#0E1217] p-2 sm:p-2.5 px-3 sm:px-4 flex flex-col md:flex-row items-stretch md:items-center justify-between gap-3 shadow-xl">
          {/* Left Tabs (Inside main card: Active gradient pill, inactive plain text) */}
          <div className="flex items-center gap-1 sm:gap-2 overflow-x-auto">
            <button
              type="button"
              onClick={() => {
                switchScope('all');
                setTypeFilter('');
              }}
              className={`px-4 py-2 rounded-xl text-xs sm:text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
                scope === 'all' && !typeFilter
                  ? 'text-black shadow-[0_0_16px_rgba(98,250,227,0.35)]'
                  : 'text-gray-400 hover:text-white hover:bg-white/[0.04]'
              }`}
              style={
                scope === 'all' && !typeFilter
                  ? { background: 'linear-gradient(106.58deg, #D0BCFF 0%, #62FAE3 100%)' }
                  : undefined
              }
            >
              All Tasks
            </button>
            <button
              type="button"
              onClick={() => {
                switchScope('stories');
                setTypeFilter('');
              }}
              className={`px-4 py-2 rounded-xl text-xs sm:text-sm font-semibold transition-all cursor-pointer flex items-center gap-2 whitespace-nowrap ${
                scope === 'stories'
                  ? 'text-black shadow-[0_0_16px_rgba(98,250,227,0.35)]'
                  : 'text-gray-400 hover:text-white hover:bg-white/[0.04]'
              }`}
              style={
                scope === 'stories'
                  ? { background: 'linear-gradient(106.58deg, #D0BCFF 0%, #62FAE3 100%)' }
                  : undefined
              }
            >
              <BookOpen className="w-3.5 h-3.5" />
              Stories
            </button>
            <button
              type="button"
              onClick={() => {
                switchScope('all');
                setTypeFilter(IMAGE_STORY_JOB_TYPE);
              }}
              className={`px-4 py-2 rounded-xl text-xs sm:text-sm font-semibold transition-all cursor-pointer flex items-center gap-2 whitespace-nowrap ${
                typeFilter === IMAGE_STORY_JOB_TYPE
                  ? 'text-black shadow-[0_0_16px_rgba(98,250,227,0.35)]'
                  : 'text-gray-400 hover:text-white hover:bg-white/[0.04]'
              }`}
              style={
                typeFilter === IMAGE_STORY_JOB_TYPE
                  ? { background: 'linear-gradient(106.58deg, #D0BCFF 0%, #62FAE3 100%)' }
                  : undefined
              }
            >
              <Image className="w-3.5 h-3.5" />
              Images
            </button>
          </div>

          {/* Right Search Input & Filters Toggle */}
          <div className="flex items-center gap-2">
            <Select
              value={sortBy}
              onValueChange={applySortBy}
            >
              <SelectTrigger
                className="w-[9.5rem] h-10 text-xs border-white/[0.08] bg-black/40 text-white rounded-xl hover:border-white/15"
                data-testid="tasks-sort-select"
              >
                <SelectValue placeholder="Sort" />
              </SelectTrigger>
              <SelectContent className="bg-[#0E1217] border-white/10 text-white">
                {TASK_SORT_OPTIONS.map((opt) => (
                  <SelectItem
                    key={opt.id}
                    value={opt.id}
                    className="hover:bg-white/10 text-xs"
                    data-testid={`tasks-sort-${opt.id}`}
                  >
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Search Input Box */}
            <div className="relative flex-1 sm:w-64 md:w-72">
              <input
                type="text"
                placeholder="Search tasks..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full h-10 pl-9 pr-8 bg-black/40 border border-white/[0.08] hover:border-white/15 focus:border-[#62FAE3]/60 focus:bg-black/60 rounded-xl text-xs sm:text-sm text-white placeholder-gray-500 transition-all outline-none"
              />
              <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white p-0.5 rounded cursor-pointer"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>

            {/* Filter Toggle Button */}
            <button
              type="button"
              onClick={() => setIsFilterDropdownOpen((prev) => !prev)}
              className={`h-10 w-10 shrink-0 rounded-xl border flex items-center justify-center transition-all cursor-pointer ${
                isFilterDropdownOpen || statusFilter || (typeFilter && typeFilter !== IMAGE_STORY_JOB_TYPE)
                  ? 'border-[#62FAE3] text-[#62FAE3] bg-[#62FAE3]/10 shadow-[0_0_10px_rgba(98,250,227,0.2)]'
                  : 'border-white/[0.08] bg-black/40 text-gray-400 hover:text-white hover:bg-white/[0.06]'
              }`}
              title="Filter Options"
            >
              <Filter className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* ── Optional Filter Controls Drawer / Dropdown ── */}
        {isFilterDropdownOpen && (
          <div className="rounded-2xl border border-white/[0.08] bg-[#0E1217] p-4 shadow-xl flex flex-wrap items-center gap-3 animate-in fade-in slide-in-from-top-2 duration-200">
            <span className="text-xs font-mono uppercase text-gray-400 font-bold">Filter By:</span>

            {/* Status Select */}
            <Select
              value={statusFilter || 'all'}
              onValueChange={(v) => applyStatusFilter(v === 'all' ? '' : v)}
            >
              <SelectTrigger className="w-40 h-9 text-xs border-white/10 bg-white/[0.04] text-white rounded-xl">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent className="bg-[#0E1217] border-white/10 text-white">
                {TASK_STATUS_FILTERS.map((opt) => (
                  <SelectItem key={opt.id || 'all'} value={opt.id || 'all'} className="hover:bg-white/10 text-xs">
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Type Select */}
            <Select
              value={typeFilter || 'all'}
              onValueChange={(v) => applyTypeFilter(v === 'all' ? '' : v)}
            >
              <SelectTrigger className="w-48 h-9 text-xs border-white/10 bg-white/[0.04] text-white rounded-xl">
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent className="bg-[#0E1217] border-white/10 text-white max-h-60">
                {typeFilterOptions.map((opt) => (
                  <SelectItem key={opt.id || 'all'} value={opt.id || 'all'} className="hover:bg-white/10 text-xs">
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={sortBy} onValueChange={applySortBy}>
              <SelectTrigger className="w-40 h-9 text-xs border-white/10 bg-white/[0.04] text-white rounded-xl" data-testid="tasks-sort-select-drawer">
                <SelectValue placeholder="Sort" />
              </SelectTrigger>
              <SelectContent className="bg-[#0E1217] border-white/10 text-white">
                {TASK_SORT_OPTIONS.map((opt) => (
                  <SelectItem key={opt.id} value={opt.id} className="hover:bg-white/10 text-xs">
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {hasActiveFilters && (
              <button
                type="button"
                onClick={clearFilters}
                className="px-3 py-1.5 rounded-lg border border-red-500/30 bg-red-500/10 text-red-400 hover:bg-red-500/20 text-xs font-semibold flex items-center gap-1 transition-colors cursor-pointer ml-auto"
              >
                <X className="w-3.5 h-3.5" />
                Clear Filters
              </button>
            )}
          </div>
        )}

        {/* ── Project Filter Banner ── */}
        {projectFilter && (
          <div className="rounded-xl border border-white/[0.08] bg-[#0E1217] px-4 py-3 flex items-center justify-between gap-3 text-xs">
            <span className="text-gray-300">
              Showing tasks for project <span className="font-mono text-[#62FAE3]">{projectFilter.slice(0, 8)}…</span>
            </span>
            <button
              type="button"
              onClick={clearProjectFilter}
              className="text-[#62FAE3] hover:underline font-semibold cursor-pointer"
            >
              Show all projects
            </button>
          </div>
        )}

        {/* ── Export Versions Summary (if any) ── */}
        {!loading && exportVersionsSummary.length > 0 && (
          <div className="rounded-2xl border border-white/[0.08] bg-[#0E1217] p-4 shadow-xl space-y-2">
            <p className="text-xs font-mono font-bold uppercase tracking-wider text-gray-400">
              All exported videos for this project ({exportVersionsSummary.length})
            </p>
            <ul className="space-y-2 max-h-48 overflow-y-auto pr-1">
              {exportVersionsSummary.map((ver, vi) => {
                const when = ver.created_at ? formatDate(ver.created_at) : '';
                if (ver.platform) {
                  const PlatformIcon = ver.platform === 'youtube' ? Youtube : Instagram;
                  const label = exportPlatformLabel(ver.platform);
                  return (
                    <li
                      key={`summary-${ver.platform}-${ver.publish_url || vi}`}
                      className="flex items-center justify-between gap-3 text-xs bg-white/[0.02] p-2.5 rounded-xl border border-white/[0.05]"
                      data-testid={`task-export-summary-${vi}-${ver.platform}`}
                    >
                      <span className="text-gray-300 truncate flex items-center gap-1.5" title={ver.title || when}>
                        <PlatformIcon className={`w-3.5 h-3.5 shrink-0 ${ver.platform === 'youtube' ? 'text-[#F87171]' : 'text-[#F472B6]'}`} />
                        Posted to {label}
                        {when ? ` · ${when}` : ''}
                        {ver.title ? ` · ${ver.title}` : ''}
                      </span>
                      {ver.publish_url ? (
                        <a
                          href={ver.publish_url}
                          target="_blank"
                          rel="noreferrer"
                          className="shrink-0 text-[#62FAE3] hover:underline font-medium"
                        >
                          Open on {label}
                        </a>
                      ) : null}
                    </li>
                  );
                }
                const fileNumber = exportVersionsSummary.slice(vi).filter((v) => !v.platform).length;
                const href = mediaUrlForPlayback(ver.url, {
                  localUrl: ver.local_url,
                  s3Url: ver.url,
                });
                const publishUrl = resolveExportVersionPublishUrl(ver);
                const ar = ver.aspect_ratio ? ` · ${ver.aspect_ratio}` : '';
                return (
                  <li key={`summary-${ver.url || vi}`} className="flex items-center justify-between gap-3 text-xs bg-white/[0.02] p-2.5 rounded-xl border border-white/[0.05]">
                    <span className="text-gray-300 truncate" title={when + ar}>
                      Export {fileNumber} {when ? ` · ${when}` : ''} {ar}
                    </span>
                    <div className="flex items-center gap-2 shrink-0">
                      {publishUrl && (
                        <TaskSocialPublishButtons
                          videoUrl={publishUrl}
                          title={`Export ${fileNumber}`}
                          onOpen={openSocialPublish}
                          testIdPrefix={`task-export-summary-${vi}`}
                          compact
                        />
                      )}
                      {href && (
                        <a
                          href={href}
                          target="_blank"
                          rel="noreferrer"
                          download
                          className="inline-flex items-center gap-1 text-[#62FAE3] hover:underline font-medium"
                        >
                          <Download className="w-3.5 h-3.5" />
                          Download
                        </a>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {/* ── Task Cards List ── */}
        {loading ? (
          <div className="flex flex-col gap-3.5">
            {[1, 2, 3].map((i) => (
              <div key={i} className="rounded-2xl border border-white/[0.07] bg-[#0E1217] p-4.5 px-6 shadow-md animate-pulse flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-white/5 shrink-0" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 w-1/3 bg-white/10 rounded-md" />
                  <div className="h-3 w-1/4 bg-white/5 rounded-md" />
                </div>
              </div>
            ))}
          </div>
        ) : filteredJobs.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-white/10 bg-[#0E1217] px-6 py-16 text-center shadow-xl space-y-4">
            <Clock className="w-12 h-12 mx-auto text-gray-600" />
            <div>
              <h3 className="text-lg font-bold text-white font-headline">
                {hasActiveFilters ? 'No tasks match your filters' : 'No tasks recorded yet'}
              </h3>
              <p className="text-xs text-gray-400 mt-1 max-w-sm mx-auto">
                {hasActiveFilters
                  ? 'Try clearing your search or filters to see all available tasks.'
                  : 'Start a story or video generation from the studio to view live task progress.'}
              </p>
            </div>
            {hasActiveFilters ? (
              <button
                type="button"
                onClick={clearFilters}
                className="px-5 py-2.5 rounded-xl border border-white/15 bg-white/5 hover:bg-white/10 text-white text-xs font-semibold transition-colors cursor-pointer"
              >
                Clear all filters
              </button>
            ) : (
              <button
                type="button"
                onClick={() => navigate('/flows')}
                className="px-5 py-2.5 rounded-xl text-black text-xs font-bold transition-all shadow-[0_0_15px_rgba(98,250,227,0.3)] cursor-pointer"
                style={{ background: 'linear-gradient(106.58deg, #D0BCFF 0%, #62FAE3 100%)' }}
              >
                Create Story
              </button>
            )}
          </div>
        ) : (
          <div className="flex flex-col gap-3.5" data-testid="tasks-job-list">
            {filteredJobs.map((job) => {
              const sc = statusConfig[job.status] || statusConfig.pending;
              const TypeIcon = typeIcons[job.job_type] || TwinneeLogo;
              const storyId = job.story_id || null;
              const learningProjectId = LEARNING_JOB_TYPES.has(job.job_type)
                ? (job.learning_project_id || job.project_id || null)
                : null;
              const pitchProjectId = job.job_type === 'pitch_recording_pipeline'
                ? (job.pitch_project_id || job.project_id || null)
                : null;
              const avatarCastProjectId = job.job_type === 'avatar_cast_pipeline'
                ? (job.avatar_cast_project_id || job.project_id || null)
                : null;
              const scriptAlignJobId = job.job_type === 'script_audio_align_pipeline'
                ? (job.script_audio_align_job_id || job.project_id || null)
                : null;
              const mascotProjectId = job.job_type === MASCOT_ANIMATION_JOB_TYPE
                ? mascotProjectIdFromJob(job)
                : null;
              const narrationProjectId = job.narration_project_id
                || (['narration_pipeline', 'narration_full_automation', 'news_video_pipeline', 'script_based_pipeline', 'song_based_pipeline', 'reels_shorts_pipeline', 'reels_shorts_render'].includes(job.job_type)
                  ? job.project_id
                  : null);
              const exportVersions = Array.isArray(job.export_versions) ? job.export_versions : [];
              const showExportVersionList =
                job.job_type === 'export' && job.status === 'completed' && exportVersions.length > 0;
              const showMediaDownload =
                job.status === 'completed' &&
                Boolean(job.result_url) &&
                VIDEO_OUTPUT_JOB_TYPES.has(job.job_type) &&
                !showExportVersionList;
              const showOpenLearningEditor = Boolean(learningProjectId);
              const showOpenVideoEditor =
                (job.job_type === 'export' && Boolean(storyId || narrationProjectId)) ||
                (job.job_type === 'narration_pipeline' && Boolean(narrationProjectId)) ||
                (job.job_type === 'narration_full_automation' && Boolean(narrationProjectId)) ||
                (job.job_type === 'news_video_pipeline' && Boolean(narrationProjectId)) ||
                (job.job_type === 'script_based_pipeline' && Boolean(narrationProjectId)) ||
                (job.job_type === 'song_based_pipeline' && Boolean(narrationProjectId)) ||
                (['reels_shorts_pipeline', 'reels_shorts_render'].includes(job.job_type) && Boolean(narrationProjectId)) ||
                (job.job_type === 'script_audio_align_pipeline' && job.status === 'completed' && Boolean(storyId)) ||
                (job.job_type === 'pitch_recording_pipeline' && job.status === 'completed' && Boolean(storyId)) ||
                (job.job_type === 'story' && Boolean(storyId) && job.status === 'completed');
              const showPitchRecordingResume =
                job.job_type === 'pitch_recording_pipeline'
                && Boolean(pitchProjectId);
              const showReelsShortsResume =
                ['reels_shorts_pipeline', 'reels_shorts_render'].includes(job.job_type)
                && Boolean(narrationProjectId);
              const showAvatarCastResume =
                job.job_type === 'avatar_cast_pipeline'
                && Boolean(avatarCastProjectId);
              const showScriptAlignResume =
                job.job_type === 'script_audio_align_pipeline'
                && Boolean(scriptAlignJobId);
              const showMascotResume =
                job.job_type === MASCOT_ANIMATION_JOB_TYPE
                && Boolean(mascotProjectId);
              const showImageStoryResume = job.job_type === IMAGE_STORY_JOB_TYPE
                && ['pending', 'running', 'completed'].includes(job.status);
              const showCancel =
                (job.status === 'pending' || job.status === 'running') &&
                CANCELLABLE_JOB_TYPES.has(job.job_type);
              const showRestart =
                (job.status === 'failed' || job.status === 'cancelled') &&
                RESTARTABLE_JOB_TYPES.has(job.job_type);
              const showSocialPublish = jobSupportsSocialPublish(job);
              const publishVideoUrl = showSocialPublish ? resolveTaskJobPublishVideoUrl(job) : '';
              const publishTitle = taskJobPublishTitle(job);
              const showOpenInAgent = canOpenJobInAgent(job);

              const openVideoEditor = () => {
                if (['reels_shorts_pipeline', 'reels_shorts_render'].includes(job.job_type) && narrationProjectId) {
                  navigate(`/reels-shorts/${narrationProjectId}/video-editor`);
                  return;
                }
                if (job.job_type === 'news_video_pipeline' && narrationProjectId) {
                  navigate(`/news/${narrationProjectId}/video-editor`);
                  return;
                }
                if (storyId) navigate(`/stories/${storyId}/video-editor`);
                else if (narrationProjectId) navigate(`/narration/${narrationProjectId}/video-editor`);
              };

              const handlePrimaryProgressAction = () => {
                if (showReelsShortsResume) {
                  navigate(`/reels-shorts/create?project=${narrationProjectId}`);
                } else if (showAvatarCastResume) {
                  try {
                    localStorage.setItem('saved_avatarcast_project_id', avatarCastProjectId);
                  } catch {
                    /* private mode */
                  }
                  navigate('/avatar-cast/studio');
                } else if (showScriptAlignResume) {
                  const qs = new URLSearchParams({ jobId: scriptAlignJobId });
                  if (storyId) qs.set('storyId', storyId);
                  navigate(`/script-audio-align?${qs.toString()}`);
                } else if (showMascotResume) {
                  navigate(mascotJobResumePath(job));
                } else if (showImageStoryResume) {
                  navigate(imageStoryJobResumePath(job.id, { storyId: imageStoryNarrationProjectId(job) }));
                } else if (showOpenVideoEditor) {
                  openVideoEditor();
                } else if (showOpenLearningEditor) {
                  navigate(`/learning/${learningProjectId}/editor`);
                } else if (showPitchRecordingResume) {
                  navigate(`/pitch/${pitchProjectId}/record`);
                } else if (storyId) {
                  navigate(`/stories/${storyId}/video-editor`);
                } else if (narrationProjectId) {
                  navigate(`/narration/${narrationProjectId}/video-editor`);
                }
              };

              const taskTitle = job.context_title || typeLabels[job.job_type] || job.job_type;
              const typeLabelText = typeLabels[job.job_type] || job.job_type;
              const dateText = formatDate(job.updated_at || job.created_at);

              return (
                <div
                  key={job.id}
                  className={`rounded-2xl border border-white/[0.07] bg-[#0E1217] hover:border-white/15 px-5 sm:px-6 py-4 shadow-md transition-all ${highlightJobId === job.id ? 'ring-2 ring-[#62FAE3]' : ''
                    }`}
                  data-testid={`task-card-${job.id}`}
                >
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4">

                    {/* Left: Thumbnail & Details */}
                    <div className="flex items-center gap-3.5 sm:gap-4 min-w-0 flex-1">
                      {/* Thumbnail Box */}
                      <div className="w-12 h-12 rounded-xl overflow-hidden bg-black/40 border border-white/10 shrink-0 relative flex items-center justify-center group shadow-sm">
                        {job.result_url && (job.job_type === 'video' || job.job_type === 'export' || job.job_type === 'scene_video') ? (
                          <Film className="w-5 h-5 text-[#62FAE3]" />
                        ) : job.preview_image_url ? (
                          <img
                            src={mediaUrlForPlayback(job.preview_image_url)}
                            alt="preview"
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-white/[0.04] to-white/[0.01]">
                            <TypeIcon className="w-5 h-5 text-gray-400 group-hover:text-white transition-colors" />
                          </div>
                        )}
                      </div>

                      {/* Info & Subtitle */}
                      <div className="min-w-0 flex-1 space-y-0.5">
                        <h3
                          className="font-headline font-semibold text-sm sm:text-[15px] text-white truncate max-w-lg"
                          title={taskTitle}
                          data-testid={`task-context-title-${job.id}`}
                        >
                          {taskTitle}
                        </h3>

                        <div className="flex items-center gap-2 text-xs text-gray-400 font-body flex-wrap">
                          <span className="text-gray-300 font-medium">{typeLabelText}</span>
                          <span className="text-gray-600">•</span>
                          <span>{job.status === 'pending' ? 'Queued' : `Updated ${dateText}`}</span>
                        </div>
                      </div>
                    </div>

                    {/* Right: Status Pill & Action Buttons (Figma Exact 2-tier right layout) */}
                    <div className="flex flex-row sm:flex-col items-center sm:items-end justify-between self-stretch sm:shrink-0 gap-2 min-h-0 sm:min-h-[48px] flex-wrap">
                      {/* Top tier: Status Badge (Figma Exact Pills) */}
                      <div className="flex items-center gap-2 justify-end">
                        {job.status === 'completed' ? (
                          <span className="inline-flex items-center gap-1.5 px-3 py-0.5 rounded-full text-xs font-semibold text-[#62FAE3] bg-[#62FAE3]/10 border border-[#62FAE3]/30 shadow-[0_0_8px_rgba(98,250,227,0.15)]">
                            <CheckCircle className="w-3.5 h-3.5" />
                            Completed
                          </span>
                        ) : job.status === 'running' ? (
                          <span className="inline-flex items-center gap-1.5 px-3 py-0.5 rounded-full text-xs font-semibold text-[#22D3EE] bg-[#22D3EE]/10 border border-[#22D3EE]/30 animate-pulse">
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            In Progress ({job.progress ?? 0}%)
                          </span>
                        ) : job.status === 'pending' ? (
                          <span className="inline-flex items-center gap-1.5 px-3 py-0.5 rounded-full text-xs font-medium text-gray-300 bg-white/[0.06] border border-white/10">
                            <div className="w-1.5 h-1.5 rounded-full bg-gray-400" />
                            Pending
                          </span>
                        ) : job.status === 'failed' ? (
                          <span className="inline-flex items-center gap-1.5 px-3 py-0.5 rounded-full text-xs font-medium text-red-400 bg-red-400/10 border border-red-400/30">
                            <XCircle className="w-3.5 h-3.5" />
                            Failed
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 px-3 py-0.5 rounded-full text-xs font-medium text-gray-400 bg-white/[0.04] border border-white/10">
                            <Clock className="w-3.5 h-3.5" />
                            Queued (Pause)
                          </span>
                        )}
                      </div>

                      {/* Bottom tier: Figma Action Links */}
                      <div className="flex items-center gap-2.5 justify-end text-xs flex-wrap">
                        {showOpenInAgent ? (
                          <button
                            type="button"
                            onClick={() => void handleOpenInAgent(job)}
                            disabled={openingAgentJobId === job.id}
                            className="text-xs font-normal text-[#62FAE3] hover:text-[#4ee8d0] flex items-center gap-1 transition-colors cursor-pointer disabled:opacity-50"
                            title="Reopen the create agent conversation that started this task"
                            data-testid={`task-open-agent-${job.id}`}
                          >
                            <MessageSquare className="w-3 h-3" />
                            <span>{openingAgentJobId === job.id ? 'Opening…' : 'Open in agent'}</span>
                          </button>
                        ) : null}
                        {/* Primary View Progress Action (Always present for active/editable jobs) */}
                        {(job.status === 'pending' || job.status === 'running' || job.status === 'awaiting_review' || showOpenVideoEditor || showImageStoryResume || showPitchRecordingResume || showReelsShortsResume || showAvatarCastResume || showScriptAlignResume || showMascotResume) && (
                          <button
                            type="button"
                            onClick={handlePrimaryProgressAction}
                            className="text-xs font-normal text-gray-300 hover:text-white flex items-center gap-1 transition-colors cursor-pointer"
                          >
                            <span>
                              {job.status === 'completed' && showImageStoryResume
                                ? 'Apply scenes'
                                : showAvatarCastResume || showScriptAlignResume || showReelsShortsResume || showMascotResume
                                  ? (job.status === 'completed' ? 'Open project' : 'View progress')
                                  : 'View progress'}
                            </span>
                            <ArrowRight className="w-3 h-3" />
                          </button>
                        )}
                        {['reels_shorts_pipeline', 'reels_shorts_render'].includes(job.job_type) && narrationProjectId ? (
                          <button
                            type="button"
                            onClick={openVideoEditor}
                            className="text-xs font-normal text-gray-300 hover:text-white flex items-center gap-1 transition-colors cursor-pointer"
                          >
                            <span>Open editor</span>
                            <ArrowRight className="w-3 h-3" />
                          </button>
                        ) : null}
                        {job.job_type === 'script_audio_align_pipeline' && storyId && job.status === 'completed' ? (
                          <button
                            type="button"
                            onClick={() => navigate(`/stories/${storyId}/video-editor`)}
                            className="text-xs font-normal text-gray-300 hover:text-white flex items-center gap-1 transition-colors cursor-pointer"
                          >
                            <span>Open editor</span>
                            <ArrowRight className="w-3 h-3" />
                          </button>
                        ) : null}

                        {/* Completed video download action */}
                        {showMediaDownload && (
                          <a
                            href={mediaUrlForPlayback(job.result_url)}
                            target="_blank"
                            rel="noreferrer"
                            download
                            className="text-xs font-normal text-gray-300 hover:text-white flex items-center gap-1 transition-colors"
                          >
                            <span>Download</span>
                            <Download className="w-3 h-3" />
                          </a>
                        )}

                        {showSocialPublish && publishVideoUrl && (
                          <TaskSocialPublishButtons
                            videoUrl={publishVideoUrl}
                            title={publishTitle}
                            jobId={job.id}
                            onOpen={openSocialPublish}
                            testIdPrefix={`task-social-${job.id}`}
                            compact
                          />
                        )}

                        {/* Cancel icon button (circle-x) as in Figma */}
                        {showCancel && (
                          <button
                            type="button"
                            disabled={cancellingId === job.id}
                            onClick={() => cancelJob(job.id)}
                            className="text-gray-400 hover:text-white transition-colors cursor-pointer p-0.5 flex items-center gap-1"
                            title="Cancel task"
                          >
                            {cancellingId === job.id ? (
                              <Loader2 className="w-3.5 h-3.5 animate-spin text-[#62FAE3]" />
                            ) : (
                              <XCircle className="w-3.5 h-3.5" />
                            )}
                          </button>
                        )}

                        {/* Retry action */}
                        {showRestart && (
                          <button
                            type="button"
                            disabled={restartingId === job.id}
                            onClick={() => restartJob(job.id)}
                            className="text-xs text-[#62FAE3] hover:text-[#4ee8d0] flex items-center gap-1 transition-colors cursor-pointer"
                          >
                            {restartingId === job.id ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : (
                              <>
                                <span>Retry</span>
                                <RefreshCw className="w-3 h-3" />
                              </>
                            )}
                          </button>
                        )}

                        {/* Extra Options icon button (3 dots) as in Figma */}
                        {job.status === 'completed' && (
                          <button
                            type="button"
                            onClick={openVideoEditor}
                            className="text-gray-400 hover:text-white p-0.5 transition-colors cursor-pointer"
                            title="More options"
                          >
                            <MoreVertical className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Exported video versions sublist */}
                  {showExportVersionList && (
                    <div className="mt-3.5 pt-3.5 border-t border-white/[0.06]">
                      <p className="text-[11px] font-mono text-gray-400 uppercase tracking-wider mb-2">
                        Exported versions ({exportVersions.length})
                      </p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {exportVersions.map((ver, vi) => {
                          const href = mediaUrlForPlayback(ver.url, {
                            localUrl: ver.local_url,
                            s3Url: ver.url,
                          });
                          return (
                            <div key={`ver-${vi}`} className="flex items-center justify-between p-2 rounded-xl bg-white/[0.02] border border-white/[0.05] text-xs">
                              <span className="text-gray-300">Version {exportVersions.length - vi}</span>
                              {href && (
                                <a
                                  href={href}
                                  target="_blank"
                                  rel="noreferrer"
                                  download
                                  className="text-[#62FAE3] hover:underline flex items-center gap-1"
                                >
                                  <Download className="w-3 h-3" />
                                  Download
                                </a>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* ── Figma Footer & Pagination (Exact Arrow Positioning) ── */}
        {!loading && total > 0 && (
          <div className="flex items-center justify-between pt-4 border-t border-white/[0.06] text-xs sm:text-sm text-gray-400 font-body">
            <div>
              Showing <span className="text-white font-medium">{rangeStart}-{rangeEnd}</span> of <span className="text-white font-medium">{total}</span> tasks
            </div>

            <div className="flex items-center gap-5">
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className={`flex items-center gap-1.5 transition-colors cursor-pointer ${page <= 1 ? 'opacity-30 cursor-not-allowed' : 'hover:text-white'
                  }`}
              >
                <ChevronLeft className="w-4 h-4" />
                Previous
              </button>

              <span className="font-mono text-white font-medium">
                Page {page} / {totalPages}
              </span>

              <button
                type="button"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                className={`flex items-center gap-1.5 transition-colors cursor-pointer ${page >= totalPages ? 'opacity-30 cursor-not-allowed' : 'hover:text-white'
                  }`}
              >
                Next
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── Dialog Modals ── */}
      <CreatePlanChatStudio
        open={Boolean(agentParam)}
        seed={agentParam === 'new' && flowParam ? { preferredFlowId: flowParam } : null}
        resumePlanId={agentParam && agentParam !== 'new' ? agentParam : null}
        navigate={navigate}
        syncUrl
        onClose={closeCreateAgent}
        onTaskCreated={() => {
          loadJobs({ silent: true });
        }}
      />
      <ScriptBasedPipelineDialog
        open={Boolean(scriptPipelineTarget)}
        onOpenChange={(open) => {
          if (!open) setScriptPipelineTarget(null);
        }}
        jobId={scriptPipelineTarget?.jobId}
        projectId={scriptPipelineTarget?.projectId}
        showCompletionToasts={false}
      />
      <SongBasedPipelineDialog
        open={Boolean(songPipelineTarget)}
        onOpenChange={(open) => {
          if (!open) setSongPipelineTarget(null);
        }}
        jobId={songPipelineTarget?.jobId}
        projectId={songPipelineTarget?.projectId}
        showCompletionToasts={false}
      />
      <YouTubePublishDialog
        open={socialPublish?.platform === 'youtube'}
        onOpenChange={(open) => {
          if (!open) setSocialPublish(null);
        }}
        oauthReturnPath={socialOAuthReturnPath}
        defaultVideoUrl={socialPublish?.videoUrl || ''}
        defaultTitle={socialPublish?.title || ''}
        scriptContext={socialPublish?.scriptContext || ''}
        storyLanguage={socialPublish?.language || ''}
        narrationProjectId={projectFilter || null}
        storyId={projectFilter || null}
        onPublished={() => loadJobs({ silent: true })}
      />
      <InstagramPublishDialog
        open={socialPublish?.platform === 'instagram'}
        onOpenChange={(open) => {
          if (!open) setSocialPublish(null);
        }}
        oauthReturnPath={socialOAuthReturnPath}
        defaultVideoUrl={socialPublish?.videoUrl || ''}
        defaultTitle={socialPublish?.title || ''}
        scriptContext={socialPublish?.scriptContext || ''}
        narrationProjectId={projectFilter || null}
        storyId={projectFilter || null}
        onPublished={() => loadJobs({ silent: true })}
      />
    </div>
  );
};
